import { CategoryItem, copyThingModel, getAllCategories, getCategoryThingModel, searchCategories, ThingModelItem } from '@/api';
import useRequest from '@/hooks/useRequest';
import { SearchOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Card, Checkbox, Form, Input, Layout, Menu, message, Modal, Select, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { get } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../ProductTemplate/index.module.less';
import { DetailModal, DetailModalRef } from './DeatilModal';
import { TypeSpecMap } from './helper';
import { ProductInfo } from './types';

const { Text, Link } = Typography;
const { Sider, Content } = Layout;
const { useForm, useWatch } = Form;

const TypeMap: { [key: string]: string } = {
  action: '服务类型',
  event: '事件类型',
  property: '属性类型',
};

export interface SolutionCenterModalRef {
  open: () => void;
}

interface SolutionCenterModalProps {
  copyToProductId?: string;
  onSelect?: (selectedItems: ThingModelItem[]) => void;
  onSuccess?: () => void;
}

export const SolutionCenterModal = React.forwardRef<SolutionCenterModalRef, SolutionCenterModalProps>(({ copyToProductId, onSelect, onSuccess }, ref) => {
  const [form] = useForm();
  const detailModalRef = useRef<DetailModalRef>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [selectedMenu, setSelectedMenu] = useState<string>('');
  const [selectedMenuObj, setSelectedMenuObj] = useState<{ name?: string }>({});
  const [selectedRowKey, setSelectedRowKey] = useState<string>('');
  const [productInfo, setProductInfo] = useState<ProductInfo | undefined>(undefined);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchResults, setSearchResults] = useState<CategoryItem[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const searchRef = useRef<any>(null);

  const nameValue = useWatch('name', form);
  const typeValue = useWatch('type', form);

  // 获取分类数据
  const { data: categoriesData, loading: categoriesLoading } = useRequest(getAllCategories);

  // 搜索分类数据
  const handleSearch = async (value: string) => {
    setSearchValue(value);
    if (!value || value.trim() === '') {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setSearchLoading(true);
    setShowSearchDropdown(true);
    try {
      const response = await searchCategories({ name: value.trim() });
      if (response.success && response.result) {
        setSearchResults(response.result);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('搜索失败:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 处理搜索结果选择 - 只处理二级分类选择
  const handleSearchSelect = (value: string, option: any) => {
    const selectedCategory = option?.category as CategoryItem;
    const parentCategoryFromOption = option?.parentCategory as CategoryItem;

    if (!selectedCategory || !parentCategoryFromOption) return;

    // 在全局数据中找到对应的父分类和子分类
    const parentCategory = categoriesData?.find((item: CategoryItem) => item.id === parentCategoryFromOption.id);
    let childCategory: CategoryItem | undefined;

    if (parentCategory && parentCategory.children) {
      childCategory = parentCategory.children.find((child: CategoryItem) => child.id === selectedCategory.id);
    }

    // 切换到对应的分类
    if (parentCategory && childCategory) {
      setActiveTab(parentCategory.id);
      setSelectedMenu(childCategory.id);
      setSelectedMenuObj({ name: childCategory.name });
    }

    // 清空搜索
    setSearchValue('');
    setShowSearchDropdown(false);
    setSearchResults([]);
  };

  // 根据分类数据生成横向tab（一级目录），按sort_order排序
  const topTabs = useMemo(() => {
    if (!categoriesData || !Array.isArray(categoriesData)) return [];
    return categoriesData
      .slice()
      .sort((a: CategoryItem, b: CategoryItem) => a.sort_order - b.sort_order)
      .map((item: CategoryItem) => ({
        key: item.id,
        label: item.name,
      }));
  }, [categoriesData]);

  // 根据选中的一级目录获取子菜单（children），按sort_order排序，并包含图片
  const currentSideMenus = useMemo(() => {
    if (!categoriesData || !activeTab) return [];
    const currentCategory = categoriesData.find((item: CategoryItem) => item.id === activeTab);
    if (!currentCategory || !Array.isArray(currentCategory.children)) return [];
    const filePath = process.env.REACT_APP_FILEPATH || '';
    const resolveUrl = (url: string) => {
      if (!url) return '';
      const encodeP = (p: string) => p.split('/').map((seg) => (/[^\x00-\x7F]/.test(seg) ? encodeURIComponent(seg) : seg)).join('/');
      if (/^https?:\/\//i.test(url)) return encodeP(url);
      if (url.startsWith('/')) return encodeP(`${window.location.origin}${url}`);
      if (/^[\w.-]+(?::\d+)?\//.test(url)) return encodeP(`${window.location.protocol}//${url}`);
      return encodeP(`${window.location.origin}/${url}`);
    };
    return currentCategory.children
      .slice()
      .sort((a: CategoryItem, b: CategoryItem) => a.sort_order - b.sort_order)
      .map((child: CategoryItem) => {
        // 构建图片URL
        const imageUrl = child.image_url ? resolveUrl(child.image_url) : null;

        return {
          key: child.id,
          label: child.name,
          image_url: imageUrl,
        };
      });
  }, [categoriesData, activeTab]);

  // 当分类数据加载完成且activeTab为空时，设置默认选中第一个tab（按sort_order排序后的第一个）
  useEffect(() => {
    if (categoriesData && categoriesData.length > 0 && !activeTab) {
      const sortedCategories = [...categoriesData].sort((a: CategoryItem, b: CategoryItem) => a.sort_order - b.sort_order);
      const firstCategoryId = sortedCategories[0].id;
      setActiveTab(firstCategoryId);
      // 如果第一个分类有子项，选中第一个子项（按sort_order排序后的第一个）
      if (sortedCategories[0].children && sortedCategories[0].children.length > 0) {
        const sortedChildren = [...sortedCategories[0].children].sort((a: CategoryItem, b: CategoryItem) => a.sort_order - b.sort_order);
        setSelectedMenu(sortedChildren[0].id);
        setSelectedMenuObj({ name: sortedChildren[0].name });
      }
    }
  }, [categoriesData, activeTab]);

  // 当切换一级目录时，自动选中第一个子菜单
  useEffect(() => {
    if (activeTab && currentSideMenus.length > 0 && !selectedMenu) {
      setSelectedMenu(currentSideMenus[0].key);
      setSelectedMenuObj({ name: currentSideMenus[0].label });
    } else if (activeTab && currentSideMenus.length === 0) {
      setSelectedMenu('');
      setSelectedMenuObj({});
    }
  }, [activeTab, currentSideMenus, selectedMenu]);

  // 获取品类物模型数据
  const {
    data: thingModelResponse,
    loading: thingModelLoading,
    run: loadThingModel,
  } = useRequest((solutionId: string) => getCategoryThingModel(solutionId), {
    manual: true,
  });

  // 当selectedMenu变化时，调用接口获取物模型数据
  useEffect(() => {
    if (selectedMenu) {
      loadThingModel(selectedMenu);
      setSelectedRowKey(''); // 重置选中行
      setSelectedRowKeys([]); // 重置复选框选中
    }
  }, [selectedMenu, loadThingModel]);

  // 当获取到物模型数据后，构建 ProductInfo 对象
  useEffect(() => {
    if (thingModelResponse && selectedMenu) {
      const { properties = [], events = [], actions = [] } = thingModelResponse;
      setProductInfo({
        id: selectedMenu, // 使用 selectedMenu 作为 solution_id
        name: thingModelResponse.name,
        properties: properties.map((item: ThingModelItem) => ({
          ...item,
          type: 'property',
          access_mode: item.access_mode || 'R',
          type_spec: {
            type: item.type_spec?.type || '',
            specs: item.type_spec?.specs || '{}',
          },
        })) as any,
        events: events.map((item: ThingModelItem) => ({
          ...item,
          type: 'event',
        })) as any,
        actions: actions.map((item: ThingModelItem) => ({
          ...item,
          type: 'action',
        })) as any,
      } as ProductInfo);
    } else if (selectedMenu && !thingModelResponse) {
      // 创建时，即使没有数据也要设置 productInfo，以便传递 solution_id
      setProductInfo({
        id: selectedMenu,
        name: '',
        properties: [],
        events: [],
        actions: [],
      } as any);
    }
  }, [thingModelResponse, selectedMenu]);

  // 将接口返回的 properties, events, actions 合并成表格数据源
  const thingModelDataSource = useMemo(() => {
    if (!thingModelResponse) return [];
    const { properties = [], events = [], actions = [] } = thingModelResponse;
    return [
      ...properties.map((item: ThingModelItem) => ({ ...item, type: 'property' as const })),
      ...events.map((item: ThingModelItem) => ({ ...item, type: 'event' as const })),
      ...actions.map((item: ThingModelItem) => ({ ...item, type: 'action' as const })),
    ];
  }, [thingModelResponse]);

  // 根据筛选条件过滤数据
  const filteredDataSource = useMemo(() => {
    if (!thingModelDataSource || !Array.isArray(thingModelDataSource)) return [];
    return thingModelDataSource.filter(({ type, name }) => {
      return type.includes(typeValue || '') && name.includes(nameValue || '');
    });
  }, [thingModelDataSource, nameValue, typeValue]);

  // 处理选择按钮点击
  const handleSelect = async () => {
    if (!copyToProductId || !selectedMenu) {
      message.error('缺少必要参数');
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个物模型');
      return;
    }

    const selectedItems = filteredDataSource.filter((item) => selectedRowKeys.includes(item.id));
    
    // 将 type 转换为接口需要的格式：property -> properties, event -> events, action -> actions
    const typeMap: { [key: string]: 'properties' | 'events' | 'actions' } = {
      property: 'properties',
      event: 'events',
      action: 'actions',
    };

    const thing_mode_copy_info = selectedItems.map((item) => ({
      thing_model_id: item.id,
      thing_model_type: typeMap[item.type] || 'properties',
    }));

    try {
      const response = await copyThingModel({
        copy_to_product_id: copyToProductId,
        copy_from_product_id: selectedMenu,
        thing_mode_copy_info,
      });

      if (response.success) {
        message.success('拷贝成功');
        onSelect?.(selectedItems);
        onSuccess?.();
        setOpen(false);
        setSelectedRowKeys([]);
      } else {
        message.error(response.errorMsg || '拷贝失败');
      }
    } catch (error: any) {
      console.error('拷贝失败:', error);
      message.error(error?.errorMsg || '拷贝失败');
    }
  };

  // 生成搜索选项 - 只显示二级分类（子分类）
  const searchOptions = useMemo(() => {
    if (!searchResults || searchResults.length === 0) {
      return [];
    }

    // 只提取二级分类（子分类）
    const extractChildCategories = (categories: CategoryItem[]): any[] => {
      const result: any[] = [];
      categories.forEach((category) => {
        if (category.children && category.children.length > 0) {
          category.children.forEach((child) => {
            result.push({
              value: child.id,
              label: `${category.name} / ${child.name}`,
              category: child,
              parentCategory: category,
            });
          });
        }
      });
      return result;
    };

    return extractChildCategories(searchResults);
  }, [searchResults]);

  // 表格列定义
  const columns = [
    {
      title: '功能类型',
      key: 'type',
      dataIndex: 'type',
      render: (value: string) => {
        return (
          <>
            {value === 'property' && <Tag color="green">{TypeMap[value]}</Tag>}
            {value === 'action' && <Tag color="blue">{TypeMap[value]}</Tag>}
            {value === 'event' && <Tag color="volcano">{TypeMap[value]}</Tag>}
          </>
        );
      },
    },
    { title: '功能名称', key: 'name', dataIndex: 'name' },
    { title: '标识符', key: 'code', dataIndex: 'code' },
    {
      title: '数据类型',
      key: 'type_spec_type',
      dataIndex: 'type_spec_type',
      render: (value: any, record: ThingModelItem) => {
        const type = get(record, 'type_spec.type', '') as string;
        return TypeSpecMap[type as keyof typeof TypeSpecMap] || '-';
      },
    },
    {
      title: '读写类型',
      key: 'access_mode',
      dataIndex: 'access_mode',
      render(value: string) {
        const map: { [key: string]: string } = {
          RW: '可下发可上报（RW）',
          R: '只上报（R）',
          W: '只下发（W）',
        };
        return map[value] || value || '-';
      },
    },
    {
      title: '数据定义',
      key: 'type_spec_specs',
      dataIndex: 'type_spec_specs',
      render: (value: any, record: ThingModelItem) => {
        const type = get(record, 'type_spec.type', '') as string;
        const specs = get(record, 'type_spec.specs', '{}') as string;

        try {
          if (type === 'array') {
            return '数组个数: ' + JSON.parse(specs)?.size;
          } else if (type === 'text') {
            return '数据长度: ' + JSON.parse(specs)?.length;
          } else if (type === 'date') {
            return '整数类型Int64的UTC时间戳 (毫秒)';
          } else if (type === 'bool' || type === 'enum') {
            return Object.entries(JSON.parse(specs))
              .map(([k, v]) => `${k}-${v}`)
              .map((text, index) => (
                <Tag color="processing" key={index}>
                  {text}
                </Tag>
              ));
          } else if (type === 'int' || type === 'float') {
            const specsObj = JSON.parse(specs);
            return (
              <>
                <div>
                  取值范围: {specsObj?.min}-{specsObj?.max}
                </div>
                <div>步长: {specsObj?.step}</div>
                <div>
                  单位: {specsObj?.unit} ({specsObj?.unitName})
                </div>
              </>
            );
          }
        } catch (e) {
          return '-';
        }
        return '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      dataIndex: 'action',
      fixed: 'right' as const,
      render: (value: any, record: ThingModelItem) => {
        // 将 ThingModelItem 转换为 ProductThingModelType 格式
        const productThingModelRecord = {
          ...record,
          type_spec: record.type_spec
            ? {
                type: record.type_spec.type,
                specs: record.type_spec.specs,
              }
            : undefined,
        } as any;

        return (
          <Space split={<Text type="secondary">/</Text>}>
            <Link
              onClick={() => {
                detailModalRef.current?.open(productThingModelRecord);
              }}
            >
              查看
            </Link>
          </Space>
        );
      },
    },
  ];

  // 在功能类型前面添加复选框列
  const columnsWithCheckbox = [
    {
      title: '',
      key: 'checkbox',
      width: 50,
      fixed: 'left' as const,
      render: (_: any, record: ThingModelItem) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record.id]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id));
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    ...columns,
  ];

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
      // 重置状态
      setSelectedRowKeys([]);
      setSelectedRowKey('');
      form.resetFields();
    },
  }));

  const handleCancel = () => {
    setOpen(false);
    setSelectedRowKeys([]);
    setSelectedRowKey('');
    form.resetFields();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      width="90%"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
      title="方案中心"
    >
      <div style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
        {/* 搜索框 */}
        <div style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0',marginBottom: '16px' }}>
          <AutoComplete
            ref={searchRef}
            style={{ width: '100%', maxWidth: 600 }}
            options={searchLoading ? [{ value: 'loading', label: '搜索中...', disabled: true }] : searchOptions}
            onSearch={handleSearch}
            onSelect={handleSearchSelect}
            value={searchValue}
            open={showSearchDropdown && (searchResults.length > 0 || searchLoading)}
            allowClear
            onClear={() => {
              setSearchValue('');
              setSearchResults([]);
              setShowSearchDropdown(false);
            }}
            onBlur={() => {
              // 延迟隐藏下拉框，以便点击选项
              setTimeout(() => {
                setShowSearchDropdown(false);
              }, 200);
            }}
            onFocus={() => {
              if (searchValue && searchResults.length > 0) {
                setShowSearchDropdown(true);
              }
            }}
          >
            <Input suffix={<SearchOutlined />} placeholder="搜索分类..." size="large" />
          </AutoComplete>
        </div>
        <Card className="" styles={{ body: { padding: 0 } }}>
          {/* 顶部导航栏 */}
          <div style={{ borderBottom: '1px solid #f0f0f0', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Spin spinning={categoriesLoading}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                  setActiveTab(key);
                  // 切换顶部导航时，自动选中第一个侧边栏菜单（按sort_order排序后的第一个）
                  const currentCategory = categoriesData?.find((item: CategoryItem) => item.id === key);
                  if (currentCategory?.children && currentCategory.children.length > 0) {
                    const sortedChildren = [...currentCategory.children].sort((a: CategoryItem, b: CategoryItem) => a.sort_order - b.sort_order);
                    setSelectedMenu(sortedChildren[0].id);
                    setSelectedMenuObj({ name: sortedChildren[0].name });
                  } else {
                    setSelectedMenu('');
                    setSelectedMenuObj({});
                  }
                }}
                items={topTabs.map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                }))}
              />
            </Spin>
            <Button type="primary" onClick={handleSelect} disabled={selectedRowKeys.length === 0}>
              选择
            </Button>
          </div>

          <Layout style={{ background: '#fff' }}>
            {/* 左侧边栏 */}
            <Sider width={200} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
              <Menu
                mode="inline"
                selectedKeys={[selectedMenu]}
                className={styles.sideMenu}
                style={{ borderRight: 0, height: '100%' }}
                items={currentSideMenus.map((menu: { key: string; label: string; image_url?: string | null }) => ({
                  key: menu.key,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {menu.image_url && (
                        <img
                          src={menu.image_url}
                          alt={menu.label}
                          style={{
                            width: 20,
                            height: 20,
                            marginRight: 8,
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            // 图片加载失败时隐藏图片
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <span>{menu.label}</span>
                    </span>
                  ),
                }))}
                onClick={({ key }) => {
                  setSelectedMenu(key as string);
                  // 找到对应的菜单项并存储 name
                  const menuItem = currentSideMenus.find((menu: { key: string; label: string; image_url?: string | null }) => menu.key === key);
                  if (menuItem) {
                    setSelectedMenuObj({ name: menuItem.label });
                  }
                }}
              />
            </Sider>

            {/* 主内容区 */}
            <Content style={{ padding: '24px', background: '#fff' }}>
              {/* 顶部操作栏 */}
              <Space className="mb15" wrap style={{ width: '100%', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Form layout="inline" form={form} initialValues={{ type: '', name: '' }}>
                  <Form.Item key="type" name="type">
                    <Select
                      placeholder="功能类型 (全部)"
                      style={{ minWidth: 180 }}
                      options={[
                        { label: '功能类型 (全部)', value: '' },
                        { label: '属性', value: 'property' },
                        { label: '事件', value: 'event' },
                        { label: '服务', value: 'action' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item key="name" name="name">
                    <Input allowClear placeholder="请输入名称搜索" style={{ width: 200 }} />
                  </Form.Item>
                </Form>
              </Space>

              {/* 数据表格 */}
              <Table
                loading={thingModelLoading}
                dataSource={filteredDataSource}
                pagination={{
                  size: 'small',
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条`,
                  total: filteredDataSource.length,
                }}
                bordered
                rowKey="id"
                size="small"
                scroll={{ x: 'max-content' }}
                rowClassName={(record) => (record.id === selectedRowKey ? styles.selectedRow : '')}
                onRow={(record) => ({
                  onClick: () => {
                    setSelectedRowKey(record.id);
                  },
                })}
                columns={columnsWithCheckbox}
              />
            </Content>
          </Layout>
        </Card>
      </div>
      <DetailModal
        ref={detailModalRef}
        productInfo={productInfo}
        onSuccess={() => {
          // 重新加载数据
          if (selectedMenu) {
            loadThingModel(selectedMenu);
          }
        }}
      />
    </Modal>
  );
});

